import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            if (token) {
                try {
                    const response = await axios.get(`${API_URL}/api/auth/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUser(response.data);
                } catch (error) {
                    console.error('Auth error:', error);
                    logout();
                }
            }
            setLoading(false);
        };
        fetchUser();
    }, [token, logout]);

    const login = async (email, password) => {
        const response = await axios.post(`${API_URL}/api/auth/login`, {
            email,
            password
        });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const value = {
        user,
        token,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isDirection: user?.role === 'direction' || user?.role === 'admin',
        isDirecteur: user?.role === 'directeur',
        hasConsolidatedAccess: user?.has_consolidated_access || user?.role === 'admin' || user?.role === 'direction',
        // Peut voir le bouton consolidé si admin/direction OU si directeur avec accès consolidé et plusieurs restaurants
        // Note: les nouveaux utilisateurs ont 'restaurants' (noms), les anciens avaient 'restaurant_ids'
        canViewConsolidated: user?.role === 'admin' || user?.role === 'direction' || 
            (user?.has_consolidated_access && (user?.restaurants?.length > 1 || user?.restaurant_ids?.length > 1))
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
